import type { UserInfo } from 'matomo-tracker-react-native';
import { useAnalyticsStore } from '../stores/analytics.store';

/**
 * Hook that provides access to the Zustand analytics store
 */
export const useAnalyticsState = () => {
  return useAnalyticsStore();
};

/**
 * Utility function to track common events with immediate execution
 */
export const trackCommonEvents = {
  /**
   * Track register success event
   * Screen: register, Category: register, Event: register_success
   */
  registerSuccess: async (userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'on click',
        category: 'register',
        name: 'register_success',
        userInfo,
      });
    }
  },

  /**
   * Track login success event
   * Screen: login, Category: login, Event: login_success
   */
  loginSuccess: async (userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'on click',
        category: 'login',
        name: 'login_success',
        userInfo,
      });
    }
  },

  /**
   * Track view campaign event
   * Screen: campaign, Category: campaign_list, Event: view_campaign
   */
  viewCampaign: async (userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'on click',
        category: 'campaign_list',
        name: 'view_campaign',
        userInfo,
      });
    }
  },

  /**
   * Track redeem success event
   * Screen: campaign, Category: campaign_redeem, Event: redeem_success
   */
  redeemSuccess: async (campaignId: string, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'on click',
        category: 'campaign_redeem',
        name: 'redeem_success',
        // Use campaign_id as label - can be passed in userInfo or as a separate dimension
        userInfo: {
          ...userInfo,
          dimension3: campaignId, // Store campaign_id in dimension3
        },
      });
    }
  },

  /**
   * Track unconsent success event
   * Screen: marketing, Category: marketing_unconsent, Event: unconsent_success
   */
  unconsentSuccess: async (userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'on click',
        category: 'marketing_unconsent',
        name: 'unconsent_success',
        userInfo,
      });
    }
  },

  /**
   * Track button click event
   */
  buttonClick: async (buttonName: string, category: string = 'UI', userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'on click',
        category,
        name: buttonName,
        userInfo,
      });
    }
  },

  /**
   * Track form submit event
   */
  formSubmit: async (formName: string, success: boolean = true, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'submit',
        category: 'Form',
        name: formName,
        userInfo: {
          ...userInfo,
          dimension1: success ? 'success' : 'failure',
        },
      });
    }
  },

  /**
   * Track search event
   */
  search: async (keyword: string, resultCount?: number, category: string = 'Search', userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackSiteSearch({
        keyword,
        category,
        count: resultCount,
        userInfo,
      });
    }
  },

  /**
   * Track purchase event
   */
  purchase: async (amount: number, currency: string = 'THB', productName?: string, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'purchase',
        category: 'Commerce',
        name: productName || 'Purchase',
        value: amount,
        userInfo: {
          ...userInfo,
          dimension1: currency,
        },
      });
    }
  },

  /**
   * Track user registration event
   */
  userRegistration: async (method: string = 'email', userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'register',
        category: 'User',
        name: `registration_${method}`,
        userInfo,
      });
    }
  },

  /**
   * Track user login event
   */
  userLogin: async (method: string = 'email', userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'login',
        category: 'User',
        name: `login_${method}`,
        userInfo,
      });
    }
  },

  /**
   * Track error event
   */
  error: async (errorType: string, errorMessage?: string, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'error',
        category: 'Error',
        name: errorType,
        userInfo: {
          ...userInfo,
          dimension2: errorMessage,
        },
      });
    }
  },

  /**
   * Track custom event
   */
  custom: async (name: string, category: string, label?: string, value?: number, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent({
        action: 'custom',
        category,
        name,
        value,
        userInfo: {
          ...userInfo,
          dimension1: label,
        },
      });
    }
  },
};

/**
 * Initialize analytics from anywhere in the app (useful for Zustand-only usage)
 */
export const initializeAnalytics = (config: {
  siteId: number;
  urlBase: string;
  trackerUrl?: string;
  userId?: string;
  disabled?: boolean;
  log?: boolean;
}) => {
  const store = useAnalyticsStore.getState();
  store.initialize(config);
};

/**
 * Global analytics instance for direct usage without React hooks
 */
export const analytics = {
  /**
   * Initialize analytics (can be called outside React components)
   */
  init: (config: {
    siteId: number;
    urlBase: string;
    trackerUrl?: string;
    userId?: string;
    disabled?: boolean;
    log?: boolean;
  }) => {
    const store = useAnalyticsStore.getState();
    store.initialize(config);
  },

  /**
   * Track event (can be called outside React components)
   */
  trackEvent: async (params: {
    action: string;
    name?: string;
    category?: string;
    value?: number;
    campaign?: string;
    userInfo?: UserInfo;
  }) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackEvent(params);
    }
  },

  /**
   * Track screen view (can be called outside React components)
   */
  trackScreenView: async (name: string, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackScreenView({ name, userInfo });
    }
  },

  /**
   * Track app start (can be called outside React components)
   */
  trackAppStart: async (userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackAppStart(userInfo);
    }
  },

  /**
   * Track site search (can be called outside React components)
   */
  trackSiteSearch: async (keyword: string, category?: string, count?: number, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackSiteSearch({ keyword, category, count, userInfo });
    }
  },

  /**
   * Track link click (can be called outside React components)
   */
  trackLink: async (link: string, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackLink({ link, userInfo });
    }
  },

  /**
   * Track download (can be called outside React components)
   */
  trackDownload: async (download: string, userInfo?: UserInfo) => {
    const store = useAnalyticsStore.getState();
    if (store.isInitialized && store.isEnabled) {
      await store.trackDownload({ download, userInfo });
    }
  },

  /**
   * Set user ID and update preset userInfo (can be called outside React components)
   */
  setUserId: (userId: string | null) => {
    const store = useAnalyticsStore.getState();
    store.setUserId(userId);
  },

  /**
   * Set custom dimension (can be called outside React components)
   */
  setCustomDimension: (id: number, value: string) => {
    const store = useAnalyticsStore.getState();
    store.setCustomDimension(id, value);
  },

  /**
   * Set preset user info (can be called outside React components)
   */
  setPresetUserInfo: (userInfo: UserInfo) => {
    const store = useAnalyticsStore.getState();
    store.setPresetUserInfo(userInfo);
  },

  /**
   * Clear preset user info (can be called outside React components)
   */
  clearPresetUserInfo: () => {
    const store = useAnalyticsStore.getState();
    store.clearPresetUserInfo();
  },

  /**
   * Enable/disable analytics (can be called outside React components)
   */
  setEnabled: (enabled: boolean) => {
    const store = useAnalyticsStore.getState();
    store.setEnabled(enabled);
  },

  /**
   * Reset analytics (can be called outside React components)
   */
  reset: () => {
    const store = useAnalyticsStore.getState();
    store.reset();
  },
};
